import React, { useRef, useEffect } from 'react'
import { useDocument } from '../store/DocumentContext'

const EditorArea: React.FC = () => {
  const { state, dispatch } = useDocument()
  const editorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.focus()
    }
  }, [])

  // Save cursor position before content update
  const saveCursorPosition = () => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      return selection.getRangeAt(0).cloneRange()
    }
    return null
  }

  // Restore cursor position after content update
  const restoreCursorPosition = (range: Range | null) => {
    if (range) {
      const selection = window.getSelection()
      if (selection) {
        selection.removeAllRanges()
        try {
          selection.addRange(range)
        } catch (e) {
          // If range is invalid, place cursor at end
          const newRange = document.createRange()
          newRange.selectNodeContents(editorRef.current!)
          newRange.collapse(false)
          selection.addRange(newRange)
        }
      }
    }
  }

  const handleInput = () => {
    if (editorRef.current) {
      const savedRange = saveCursorPosition()
      const htmlContent = editorRef.current.innerHTML
      const textContent = editorRef.current.innerText
      
      dispatch({ type: 'SET_CONTENT', payload: htmlContent })
      
      // Update word and character counts using text content
      const words = textContent.trim() ? textContent.trim().split(/\s+/).length : 0
      const characters = textContent.length
      dispatch({ 
        type: 'UPDATE_COUNTS', 
        payload: { wordCount: words, characterCount: characters } 
      })

      // Restore cursor position after state update
      setTimeout(() => {
        restoreCursorPosition(savedRange)
      }, 0)
    }
  }

  // Update editor content when state changes (but preserve cursor)
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== state.content) {
      const savedRange = saveCursorPosition()
      editorRef.current.innerHTML = state.content || ''
      restoreCursorPosition(savedRange)
    }
  }, [state.content])

  const getEditorStyle = () => ({
    fontFamily: state.fontFamily,
    fontSize: `${state.fontSize}px`,
    fontWeight: state.isBold ? 'bold' : 'normal',
    fontStyle: state.isItalic ? 'italic' : 'normal',
    textDecoration: state.isUnderline ? 'underline' : 'none',
    textAlign: state.alignment,
    lineHeight: state.lineHeight,
  })

  return (
    <div className="flex-1 flex flex-col bg-gray-50 p-4">
      <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
        {/* Document Title */}
        <div className="border-b border-gray-200 p-4 flex-shrink-0">
          <input
            type="text"
            value={state.title}
            onChange={(e) => dispatch({ type: 'SET_TITLE', payload: e.target.value })}
            className="w-full text-2xl font-semibold text-gray-900 border-none outline-none bg-transparent"
            placeholder="Document Title"
          />
        </div>

        {/* Editor Content */}
        <div className="flex-1 p-6 bg-gray-100 overflow-y-auto">
          <div
            ref={editorRef}
            contentEditable
            onInput={handleInput}
            style={{
              ...getEditorStyle(),
              maxWidth: '8.5in',
              margin: '0 auto',
              backgroundColor: 'white',
              minHeight: '500px',
              height: 'auto',
              padding: '60px',
              boxShadow: '0 0 10px rgba(0,0,0,0.1)',
              border: '1px solid #ddd'
            }}
            className="outline-none resize-none legal-document-editor"
            suppressContentEditableWarning={true}
            data-placeholder="Start typing your document..."
          />
        </div>
      </div>

      {/* Document Info */}
      <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
        <div className="flex items-center gap-4">
          <span>Words: {state.wordCount}</span>
          <span>Characters: {state.characterCount}</span>
          <span>Font: {state.fontFamily} {state.fontSize}pt</span>
        </div>
        <div className="flex items-center gap-2">
          {state.isModified && (
            <span className="text-orange-600">• Modified</span>
          )}
          {state.lastSaved && (
            <span>Last saved: {state.lastSaved.toLocaleTimeString()}</span>
          )}
        </div>
      </div>
    </div>
  )
}

export default EditorArea

